// =====================================================
// FACE VERIFICATION API CLIENT
//
// Thin wrapper around /api/face, following the same shape as the other clients
// in this folder (same BASE_URL rule, one exported object, errors surfaced
// rather than swallowed).
//
// TWO THINGS ARE DELIBERATELY ABSENT
//   • No auth header. Face verification happens BEFORE the account can log in,
//     so there is no token yet — the account is identified by its email.
//   • No AWS credential, ever. This file imports no AWS SDK and holds no key,
//     secret or collection id. Camera frames go to our own server, which makes
//     every Rekognition call for the identity half of the check.
//
// WHAT THE BROWSER *DOES* NEED FROM AWS
//   The liveness half is different, because AWS Face Liveness works by having
//   the BROWSER stream its video straight to Rekognition. For that the page
//   needs two public values — the region and a Cognito identity pool id — and
//   it fetches them from `config()` below rather than having them baked into
//   the bundle, so they live in the backend .env with everything else. The pool
//   id is public by design: it buys short-lived guest credentials that can do
//   nothing but start a liveness session. No key of ours is ever sent.
// =====================================================

import axios from "axios";

export const BASE_URL =
  window.location.hostname === "localhost"
    ? "http://localhost:5000"
    : "https://success-blog-app-backend.onrender.com";

// A camera frame is small, but a cold backend (the free tier sleeps) can take
// a while to answer the first call, and Rekognition itself is not instant.
const TIMEOUT_MS = 30000;

const client = axios.create({ baseURL: BASE_URL, timeout: TIMEOUT_MS });

// The API answers a REFUSED frame with 200 + { ok:false, code, message, hint },
// because a refusal is a normal outcome of a verification flow rather than an
// HTTP error. Genuine faults (AWS down, expired session) come back non-2xx.
// This turns both into the same object, so the component has one thing to
// render and never has to branch on transport details.
function unwrap(promise, fallbackMessage) {
  return promise
    .then((r) => r.data)
    .catch((err) => {
      const data = err?.response?.data;
      if (data && typeof data === "object") {
        return {
          ok: false,
          code: data.code || "REQUEST_FAILED",
          message: data.message || fallbackMessage,
          hint: data.hint || "",
          retryable: data.retryable !== false,
          restart: data.restart === true,
          status: err.response.status,
        };
      }
      // No response at all: the device is offline, the request timed out, or
      // the server is unreachable. Each needs its own wording — "check your
      // connection" is unhelpful when the connection is fine and we timed out.
      const offline = typeof navigator !== "undefined" && navigator.onLine === false;
      const timedOut = err?.code === "ECONNABORTED";
      return {
        ok: false,
        code: offline ? "OFFLINE" : timedOut ? "TIMEOUT" : "NETWORK_ERROR",
        message: offline
          ? "You appear to be offline."
          : timedOut
          ? "That took too long to respond."
          : "We couldn't reach the server.",
        hint: offline
          ? "Reconnect to the internet and try again."
          : "Please check your connection and try again.",
        retryable: true,
      };
    });
}

export const faceApi = {
  // Is AWS Face Liveness switched on for this deployment, and with which
  // region / identity pool? Asked once, before the camera is opened, because
  // the answer decides whether the liveness stage runs at all.
  config: () =>
    unwrap(client.get("/api/face/config"), "Could not load the verification settings."),

  // Ask AWS (through our server) for a liveness session the browser can stream
  // to. Returns { livenessSessionId }.
  createLiveness: (sessionId) =>
    unwrap(
      client.post("/api/face/liveness/create", { sessionId }),
      "Could not start the liveness check."
    ),

  // The stream finished — what did AWS decide? The verdict is read from AWS by
  // our server; this call carries no result of its own, only the session it
  // belongs to, so a browser cannot report a pass it did not earn.
  checkLiveness: (sessionId) =>
    unwrap(
      client.post("/api/face/liveness/check", { sessionId }),
      "Could not complete the liveness check."
    ),

  // Is this account required to verify, and has it already?
  status: (email) =>
    unwrap(
      client.get("/api/face/status", { params: { email } }),
      "Could not check verification status."
    ),

  // Open an attempt. Returns the first step; the rest arrive one at a time.
  startSession: (email) =>
    unwrap(client.post("/api/face/session", { email }), "Could not start face verification."),

  // Submit one frame for whichever step the server is currently expecting.
  // `image` is a data URI straight from canvas.toDataURL.
  submitStep: (sessionId, image) =>
    unwrap(client.post("/api/face/step", { sessionId, image }), "Could not check that photo."),
};

export default faceApi;
